import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useResizeObserver } from '@react-aria/utils';
import type { CollectionBase } from '@react-types/shared';
import { cx } from 'classix';

import type { NavProps } from './Nav';
import { NavigationContext } from './NavigationContext';
import { NavigationList } from './NavigationList';

import styles from './styles/Navigation.module.css';

type NavigationProps<T extends object> = CollectionBase<T> & {
	title: string;
	kind?: NavProps['kind'];
	role?: string;
	'data-test-id'?: string;
	className?: string;
};

type NavigationState = {
	collapsed: boolean;
};

/**
 * @deprecated use `Tabs` from `@launchpad-ui/components` instead
 *
 * https://launchpad.launchdarkly.com/?path=/docs/components-navigation-tabs--docs
 */
const Navigation = <T extends object>(props: NavigationProps<T>) => {
	const { children, className, 'data-test-id': testId = 'navigation' } = props;
	const wrapperRef = useRef<HTMLDivElement>(null);
	const itemListRef = useRef<HTMLDivElement>(null);
	const [shouldCollapse, setShouldCollapse] = useState(false);
	const shouldCollapseRef = useRef(shouldCollapse);
	const needsMeasureRef = useRef(false);

	useLayoutEffect(() => {
		shouldCollapseRef.current = shouldCollapse;
		if (!needsMeasureRef.current) {
			return;
		}
		needsMeasureRef.current = false;
		const wrapper = wrapperRef.current;
		const itemList = itemListRef.current;
		if (!wrapper || !itemList) {
			setShouldCollapse(false);
			return;
		}
		const nav = wrapper.querySelector('nav');
		setShouldCollapse(!!(nav && nav.scrollWidth > nav.offsetWidth));
	}, [shouldCollapse]);

	const checkShouldCollapse = useCallback(() => {
		if (shouldCollapseRef.current) {
			needsMeasureRef.current = true;
			setShouldCollapse(false);
			return;
		}
		const wrapper = wrapperRef.current;
		const itemList = itemListRef.current;
		if (!wrapper || !itemList) {
			setShouldCollapse(false);
			return;
		}
		const nav = wrapper.querySelector('nav');
		setShouldCollapse(!!(nav && nav.scrollWidth > nav.offsetWidth));
	}, []);

	useLayoutEffect(() => {
		checkShouldCollapse();
	}, [children, checkShouldCollapse]);

	useResizeObserver({ ref: wrapperRef, onResize: checkShouldCollapse });
	return (
		<div
			className={cx(styles.Navigation, shouldCollapse && styles['Navigation--collapsed'], className)}
			data-test-id={testId}
		>
			<NavigationContext.Provider value={{ shouldCollapse }}>
				<NavigationList {...props} wrapperRef={wrapperRef} itemListRef={itemListRef} />
			</NavigationContext.Provider>
		</div>
	);
};

export { Navigation };
export type { NavigationProps, NavigationState };
